/**
 * Preference change cross-tab bridge (web transport) — T12.5.1 (G).
 *
 * The browser half of the bridge: a `BroadcastChannel` that publishes a
 * "preference changed" signal (posted by shared preference mutations via the
 * registered poster) and a hook that subscribes a badge surface to it, so a
 * preference set in another tab/window refreshes this tab's badges without a
 * reload. `BroadcastChannel` is a browser global the shared package may not
 * reference (CP1), so the transport lives here.
 *
 * Cross-DEVICE propagation rides the realtime SSE path (the sync engine's job);
 * the same-device multi-tab case is the confirmed live failure this closes.
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  setPreferenceChangePoster,
  invalidatePreferenceBadgeQueries,
} from '@shopista/shared/api';

const CHANNEL_NAME = 'shopista:preferences';
const MESSAGE = 'preference-changed';

function hasBroadcastChannel(): boolean {
  return typeof BroadcastChannel !== 'undefined';
}

// Register the cross-tab poster with the shared layer at app startup, so every
// preference mutation publishes to other tabs. Calling it again just re-registers
// the same poster; a transport failure never breaks the mutation.
export function installPreferenceBroadcast(): void {
  if (!hasBroadcastChannel()) return;
  setPreferenceChangePoster(() => {
    try {
      const channel = new BroadcastChannel(CHANNEL_NAME);
      channel.postMessage(MESSAGE);
      channel.close();
    } catch {
      // Locked-down contexts can throw — never break the mutation.
    }
  });
}

// Subscribe a badge surface (Deals / Planning) to other tabs' preference changes;
// invalidates this tab's badge-input queries on receipt. Mount once per surface.
export function usePreferenceBroadcastSync(): void {
  const qc = useQueryClient();
  useEffect(() => {
    if (!hasBroadcastChannel()) return;
    let channel: BroadcastChannel;
    try {
      channel = new BroadcastChannel(CHANNEL_NAME);
    } catch {
      return;
    }
    const onMessage = (e: MessageEvent): void => {
      if (e.data === MESSAGE) invalidatePreferenceBadgeQueries(qc);
    };
    channel.addEventListener('message', onMessage);
    return () => {
      channel.removeEventListener('message', onMessage);
      channel.close();
    };
  }, [qc]);
}
